import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const BG_IMAGE = "/devanantasectors.avif";

type Hotspot = {
  id: string;
  xPct: number;
  yPct: number;
  mobileXPct?: number;
  mobileYPct?: number;
  label: string;
  solutionId: string;
};

const HOTSPOTS: Hotspot[] = [
  { id: "uav", mobileXPct: 80, mobileYPct: 35, xPct: 78, yPct: 30, label: "Unmanned Aerial Vehicles", solutionId: "UAV" },
  { id: "datacentre", mobileXPct: 72, mobileYPct: 22, xPct: 70, yPct: 15, label: "Data Centre", solutionId: "Data Center" },
  { id: "ev", xPct: 52, yPct: 60, label: "EV Infrastructure", solutionId: "EV Infrastructure" },
  { id: "agriculture", xPct: 18, yPct: 50, label: "Agriculture", solutionId: "Agriculture" },
  { id: "tools", xPct: 81, yPct: 50, label: "Industrial Power Tools", solutionId: "Tools" },
  { id: "ship", mobileXPct: 85, mobileYPct: 75, xPct: 82, yPct: 88, label: "Marine Solutions", solutionId: "Ships" },
  { id: "bess", xPct: 30, yPct: 10, label: "BESS Solutions", solutionId: "BESS" },
];

const AnimatedCity: React.FC = () => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  const [imgMetrics, setImgMetrics] = useState({
    w: 0,
    h: 0,
    offsetX: 0,
    offsetY: 0,
  });

  const handleHotspotClick = (solutionId: string) => {
    navigate(`/solutions?solution=${encodeURIComponent(solutionId)}`);
  };

  const measure = () => {
    if (!imgRef.current) return;

    const img = imgRef.current;
    const rect = img.getBoundingClientRect();

    const mobile = window.innerWidth < 1025;
    setIsMobile(mobile);

    setImgMetrics({
      w: rect.width,
      h: rect.height,
      offsetX: 0,
      offsetY: 0,
    });
  };

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, []);

  return (
    <div className="relative w-full overflow-hidden bg-black md:h-[90vh]">
      <img
        ref={imgRef}
        src={BG_IMAGE}
        onLoad={measure}
        className="w-full h-auto md:h-full object-cover pointer-events-none"
        alt="Devananta R&D Ecosystem"
      />

      {HOTSPOTS.map((spot) => {
        const xPct = isMobile && spot.mobileXPct !== undefined ? spot.mobileXPct : spot.xPct;
        const yPct = isMobile && spot.mobileYPct !== undefined ? spot.mobileYPct : spot.yPct;

        return (
          <div
            key={spot.id}
            className="absolute group cursor-pointer"
            style={{
              left: (xPct / 100) * imgMetrics.w,
              top: (yPct / 100) * imgMetrics.h,
              transform: "translate(-50%, -50%)",
            }}
            onClick={() => handleHotspotClick(spot.solutionId)}
          >
            <span className="absolute inset-0 rounded-full bg-emerald-400/40 animate-ping scale-75 sm:scale-100" />

            <div
              className="
                relative z-10
                w-4 h-4 sm:w-5 sm:h-5 md:w-7 md:h-7 lg:w-8 lg:h-8
                rounded-full
                bg-emerald-500 text-white
                flex items-center justify-center
                text-xs sm:text-sm md:text-base lg:text-xl
                font-semibold
                shadow-lg
                transition-all duration-300
                group-hover:bg-emerald-600
                group-hover:scale-110
              "
            >
              +
            </div>

            <div
              className="
                absolute left-6 sm:left-8 top-1/2
                -translate-y-1/2
                opacity-0
                translate-x-0
                group-hover:opacity-100
                group-hover:translate-x-1
                transition-all duration-300
                pointer-events-none
                hidden md:block
              "
            >
              <div className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-400 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full shadow-lg whitespace-nowrap">
                <span className="text-xs sm:text-sm font-medium">{spot.label}</span>
                <span className="text-sm sm:text-lg">→</span>
              </div>
            </div>

            <div className="absolute -top-8 left-1/2 -translate-x-1/2 md:hidden bg-emerald-500 text-white text-xs px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
              {spot.label}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AnimatedCity;